import { get_token } from "@/router";
import {
  get_active_organization_id,
  get_active_profile_id,
  get_organization_branding,
  get_organization_profile_branding,
  parse_json_or_null,
} from "@/utils/organization_profile";

export function get_branding_style(branding) {
  if (!branding) return {};

  return ({
    "--primary-color": branding.primary_color,
    "--secondary-color": branding.secondary_color,
    "--accent-color": branding.primary_color,
  });
}

export function html_to_plain_text(value) {
  const text = String(value || "").trim();
  if (!text) return "";

  return parser_is_available()
    ? parse_html_text(text)
    : strip_html_tags(text);
}

function parser_is_available() {
  return typeof DOMParser !== "undefined";
}

function parse_html_text(text) {
  const doc = new DOMParser().parseFromString(text, "text/html");
  return (doc.body?.textContent || "").trim();
}

function strip_html_tags(text) {
  return text.replace(/<[^>]*>/g, "").trim();
}

export const homeFilterMethods = {
  set_active_profile(profile) {
    this.active_profile = profile || null;
    this.saved_branding = this.get_saved_branding();
    this.apply_branding_to_platform(this.saved_branding);
  },
  get_branding_storage_key() {
    return `organization_branding_${this.user_details?.user_id || 'current'}`;
  },
  get_branding_context() {
    const context = {};
    context.organization_id = this.company?.id || this.company?.company_id || this.user_details?.organization_id || get_active_organization_id();
    context.profile_id = this.get_active_profile_id();
    context.user_id = this.user_details?.user_id;
    return context;
  },
  get_active_profile_id() {
    return [
      get_active_profile_id(this.active_profile || {}),
      this.company?.base_profile?.id,
      this.company?.profile_id,
    ].find(Boolean) || null;
  },
  get_saved_branding() {
    return [
      this.get_context_branding(),
      this.get_profile_branding(),
      this.get_stored_branding(),
    ].find(Boolean) || null;
  },
  get_context_branding() {
    return get_organization_branding(this.get_branding_context());
  },
  get_profile_branding() {
    return get_organization_profile_branding(
      this.get_branding_company(),
      this.user_details || {}
    );
  },
  get_branding_company() {
    return this.company?.id
      ? this.company
      : this.active_profile?.company || this.active_profile || {};
  },
  get_stored_branding() {
    return parse_json_or_null(localStorage.getItem(this.get_branding_storage_key()));
  },
  clear_branding_overrides() {
    const root = document.documentElement;
    [
      "--primary-color",
      "--secondary-color",
      "--tertiary-color",
      "--accent-color",
    ].forEach((property) => {
      root.style.removeProperty(property);
    });
  },
  apply_branding_to_platform(branding) {
    if (!branding) {
      this.clear_platform_branding();
      return;
    }

    this.apply_platform_branding_colors(branding);
    this.save_platform_branding_logo(branding.logo_preview);
  },
  clear_platform_branding() {
    this.clear_branding_overrides();
    localStorage.removeItem("organization_logo");
  },
  apply_platform_branding_colors(branding) {
    const root = document.documentElement;
    this.set_platform_primary_color(root, branding.primary_color);
    this.set_platform_color(root, "--secondary-color", branding.secondary_color);
    this.set_platform_color(root, "--tertiary-color", branding.tertiary_color);
  },
  set_platform_primary_color(root, color) {
    if (!color) return;
    root.style.setProperty("--primary-color", color);
    root.style.setProperty("--accent-color", color);
  },
  set_platform_color(root, property, color) {
    if (!color) return;
    root.style.setProperty(property, color);
  },
  save_platform_branding_logo(logo_preview) {
    if (!logo_preview) return;
    localStorage.setItem("organization_logo", logo_preview);
  },
  handle_organization_branding_updated(event) {
    if (event?.detail?.reset) {
      this.saved_branding = null;
      this.apply_branding_to_platform(null);
      return;
    }

    this.saved_branding = event?.detail || null;
    this.apply_branding_to_platform(this.saved_branding);
  },
  fetch_active_profile() {
    if (!get_token()) return;

    $.ajax({
      url: `${process.env.VUE_APP_BACKEND_API_URL}/company_management/get_active_profile/`,
      type: "GET",
      headers: {
        Authorization: `Token ${get_token()}`,
        "Content-Type": "application/json",
      },
      success: (response) => {
        if (response.status === "success") {
          this.set_active_profile(response.data);
        }
      },
    });
  },
  go_to_organization_profile() {
    this.$router.push("/organization_profile_setup");
  },
  view_public_profile() {
    this.$router.push("/profile");
  },
  create_post() {
    sessionStorage.setItem("open_create_feed_modal", "true");
    this.$router.push("/home");
  },
  create_announcement() {
    this.create_post();
  },
  open_create_portal_modal() {
    this.$nextTick(() => {
      const modal_element = document.getElementById("portal_modal");
      if (!modal_element || !window.bootstrap?.Modal) return;

      window.bootstrap.Modal.getOrCreateInstance(modal_element).show();
    });
  },
  create_event() {
    this.$router.push("/view_events");
  },
  create_discussion() {
    sessionStorage.setItem("home_active_tab", "top_loans_info");
    this.$router.push("/home");
  },
  create_poll() {
    sessionStorage.setItem("home_active_tab", "top_loans_info");
    this.$router.push("/home");
  },
  create_survey() {
    sessionStorage.setItem("home_active_tab", "top_loans_info");
    this.$router.push("/home");
  },
  manage_members() {
    this.$router.push({ name: "organization_members" });
  },

  fetch_company_profile() {
    const organization_id = this.user_details?.organization_id || get_active_organization_id();
    if (!organization_id || !get_token()) return;
    $.ajax({
      url: `${process.env.VUE_APP_BACKEND_API_URL}/company_management/get_company_profile/`,
      type: "POST",
      headers: {
        Authorization: `Token ${get_token()}`,
        "Content-Type": "application/json",
      },
      data: JSON.stringify({ organization_id }),
      success: (response) => {
        if (response.status === "success") {
          this.company = response.data.company_data || {};
          this.saved_branding = this.get_saved_branding();
          this.apply_branding_to_platform(this.saved_branding);
        }
      },
      error: () => {
        this.company = {};
      }
    });
  },

  get_company_logo() {
    return this.company?.base_profile?.profile_files[0]?.file?.file_url || "";
  },
  get_company_type() {
    return (
      this.company?.type_of_organization?.type_of_organization
        ?.replace(/_/g, " ") || ""
    );
  }
};
